import { FC, useCallback, useMemo, useRef, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { View, ViewType } from "@common/types/views/views";
import {
    ClientsWorkOverviewView,
    ClientsWorkOverviewViewSchema,
} from "@common/types/views/clientsWorkOverviewView";
import { omitBy } from "@common/helpers/objects";
import { getLocalTodayDateOnly } from "@helpers/date";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useCloseLiveUpdates } from "@hooks/useCloseLiveUpdates";
import { useServiceTemplateSchedule } from "@api/serviceTemplateSchedule.api";
import { ServiceTemplateScheduleOccurrencesDrawer } from "@components/settings/OrganizationCloseTemplates/ServiceTemplateScheduleOccurrencesDrawer";
import { DataGrid, type DataGridHandle } from "@design-system/DataGrid";
import {
    hasPaginationControls,
    Pagination,
} from "@design-system/Pagination/Pagination";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { ViewToolbar } from "@components/views/ViewToolbar/ViewToolbar";
import { useAssignedToMeViewLabels } from "@components/views/useAssignedToMeViewLabels";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { useClientsWorkOverviewViewStore } from "@hooks/view/useClientsWorkOverviewViewStore";
import { useViewCollection } from "@hooks/view/useViewCollection";
import { useSubNavigationPortalContent } from "@hooks/useSubNavigationPortal";
import interfaceStyles from "@components/mainInterface/MainInterface.module.scss";
import {
    FiltersChangeFn,
    FilterTabs,
    resetPayload,
} from "@components/pageHeader/filters/lib";
import { useActiveFilters } from "@components/pageHeader/filters/useActiveFilters";
import { PageCoverLoader } from "@design-system/PageCoverLoader/PageCoverLoader";
import { useGoToTemplate } from "@hooks/useGoToTemplate";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useScope } from "@state/scopeContext.lib";
import { useBusyClosesDemoWorkRows } from "@components/demo/busyClosesDemo/useBusyClosesDemo";
import { ClientsWorkOverviewCallbacks } from "./WorkRowActions";
import { ClientsWorkEmptyView } from "./ClientsWorkEmptyView";
import { ClientsWorkFooter, getClientsWorkTotal } from "./ClientsWorkFooter";
import { useWorkBoardLayout } from "./board/useWorkBoardLayout";
import { WorkBoardContent } from "./board/WorkBoardContent";
import { useWorkOverviewRows } from "./useWorkOverviewRows";
import { useActiveWorkItem } from "./detailPane/useActiveWorkItem";
import { WorkDetailLayout } from "./detailPane/WorkDetailLayout";
import { workPageViewConfig } from "./workPageViewConfig";
import { useBuiltInClientsWorkViews } from "./views/BuiltInClientsWorkViews";
import {
    useWorkPageLanding,
    type WorkPageLanding,
} from "./views/useWorkPageLanding";
import { ASSIGNED_TO_ME_WORK_VIEW_ID } from "./views/workViewIds";
import { ALL_WORK_VIEW } from "./views/kickWorkViews";
import { WorkListTable } from "./list/WorkListTable";
import { buildWorkQueryFilters, WORK_PAGE_SIZE } from "./workQueryFilters";
import { GROUP_BY_OPTIONS, useGroupedTableData } from "./useGroupedTableData";
import { useWorkViewSort } from "./useWorkViewSort";
import { WorkGroupingControls } from "./WorkGroupingControls";
import { applyWorkDeliveryDatePreset } from "./workDatePresets";
import { useWorkRowSelection } from "./useWorkRowSelection";
import { useWorkCommands } from "./commands/useWorkCommands";
import {
    getWorkOpenClientLink,
    getWorkViewInActionsLink,
} from "./workItemLinks";

const DEFAULT_PAGE = 1;

interface Filters {
    search: string;
}

const EMPTY_FILTERS: Filters = { search: "" };
const NO_CLIENT_IDS: readonly string[] = [];
const NO_MEMBER_IDS: readonly number[] = [];

const AVAILABLE_FILTERS: FilterTabs[] = [
    FilterTabs.WORK_CLIENT,
    FilterTabs.CLOSE_DATE,
    FilterTabs.WORK_START_DATE,
    FilterTabs.WORK_DELIVERY_DATE,
    FilterTabs.DAYS_IN_STAGE,
    FilterTabs.ORG_CLOSE_STAGE,
    FilterTabs.CLOSE_STATUS,
    FilterTabs.ORG_SERVICE,
    FilterTabs.CLOSE_TEMPLATE,
    FilterTabs.APPLICATION_TYPE,
    FilterTabs.PROGRESS,
    FilterTabs.HEALTH,
    FilterTabs.ENGAGEMENT_TEAM,
    FilterTabs.UNREAD,
];

const ClientsWorkContent: FC<{ landing: WorkPageLanding }> = ({
    landing: { landingViewId, initialBaseViewId, initialFilters },
}) => {
    const activeOrganization = useActiveOrganization();
    const handleGoToTemplate = useGoToTemplate();
    const navigate = useStateAwareNavigate();
    useCloseLiveUpdates();
    const [page, setPage] = useState(DEFAULT_PAGE);
    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [occurrencesScheduleId, setOccurrencesScheduleId] = useState<
        string | null
    >(null);
    const { data: occurrencesSchedule } = useServiceTemplateSchedule({
        organizationId: activeOrganization?.id,
        scheduleId: occurrencesScheduleId,
    });

    const builtInViews = useBuiltInClientsWorkViews();
    const viewLabels = useAssignedToMeViewLabels(ASSIGNED_TO_ME_WORK_VIEW_ID);
    const { view, setView } = useClientsWorkOverviewViewStore({
        cacheKey: ORGANIZATION_USER_CACHE_KEYS.CLIENTS_WORK_PAGE_VIEW_CACHE_KEY,
        initialView: builtInViews.find(({ id }) => id === landingViewId),
        initialFilters,
    });
    const viewCollection = useViewCollection({
        viewType: ViewType.CLIENTS_WORK_OVERVIEW,
        builtInViews,
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.CLIENTS_WORK_PAGE_BASE_VIEW_ID_CACHE_KEY,
    });
    const today = getLocalTodayDateOnly();

    // Tab filters live in view.filters so the diff against the base view
    // marks the view dirty and they get persisted when saving a view.
    const tabFilters = useMemo(
        () => applyWorkDeliveryDatePreset({ filters: view.filters, today }),
        [view.filters, today],
    );

    const handleViewChange = useCallback(
        (newView: View) => {
            const typed = ClientsWorkOverviewViewSchema.parse(newView);
            // Strip the bulkActions override before persisting — we always
            // inject it at render time via viewWithBulkActions.
            setView({
                ...typed,
                options: { ...typed.options, bulkActions: false },
            });
        },
        [setView],
    );

    // Selecting a saved/built-in view replaces the filters, so pagination
    // must be reset alongside it.
    const handleViewSelect = useCallback(
        (selectedView: ClientsWorkOverviewView) => {
            setView(selectedView);
            setPage(DEFAULT_PAGE);
        },
        [setView],
    );

    const handleSearchChange = useCallback((search: string | undefined) => {
        setFilters((prev) => ({ ...prev, search: search ?? "" }));
        setPage(DEFAULT_PAGE);
    }, []);

    const handleTabFiltersChange: FiltersChangeFn = useCallback(
        (partial) => {
            setView((prev) => ({
                ...prev,
                filters: omitBy(
                    { ...prev.filters, ...partial },
                    (value) => value === null || value === undefined,
                ),
            }));
            setPage(DEFAULT_PAGE);
        },
        [setView],
    );

    const activeTabFilters = useActiveFilters(tabFilters, AVAILABLE_FILTERS);
    const hasActiveFilters =
        filters.search.trim().length > 0 || activeTabFilters.length > 0;

    const handleClearFilters = useCallback(() => {
        setFilters(EMPTY_FILTERS);
        handleTabFiltersChange(
            activeTabFilters.reduce(
                (acc, filterId) => ({ ...acc, ...resetPayload[filterId] }),
                {},
            ),
        );
    }, [activeTabFilters, handleTabFiltersChange]);

    const handleGoToTemplates = useCallback(() => {
        navigate("/templates/services");
    }, [navigate]);

    const resetPage = useCallback(() => setPage(DEFAULT_PAGE), []);
    const { sort, handleSortChange } = useWorkViewSort({
        view,
        setView,
        onSortChange: resetPage,
    });

    const slimParams = useMemo(
        () => ({
            organizationId: activeOrganization?.id ?? "",
            page,
            limit: WORK_PAGE_SIZE,
            sort,
            filters: buildWorkQueryFilters({
                search: filters.search,
                tabFilters,
            }),
        }),
        [activeOrganization?.id, page, sort, filters.search, tabFilters],
    );

    const {
        workspaceRows: fetchedWorkspaceRows,
        isRefetching,
        areRowsCurrent,
        isError,
        retry,
        pageCount,
        clientCount,
    } = useWorkOverviewRows(slimParams);

    const demoFilters = useMemo(
        () => ({
            search: filters.search,
            clientIds: tabFilters.workClientIds ?? NO_CLIENT_IDS,
            memberUserIds: tabFilters.engagementTeamMemberIds ?? NO_MEMBER_IDS,
        }),
        [
            filters.search,
            tabFilters.workClientIds,
            tabFilters.engagementTeamMemberIds,
        ],
    );
    const workspaceRows = useBusyClosesDemoWorkRows({
        workspaceRows: fetchedWorkspaceRows,
        filters: demoFilters,
    });

    const { groupBy, expanded, setExpanded, handleGroupByChange, tableData } =
        useGroupedTableData({
            workspaceRows,
            view,
            setView,
            sort,
            groupByOptions: GROUP_BY_OPTIONS,
        });

    const clearRowSelection = useCallback(() => setRowSelection({}), []);
    const { isBoardLayout, toolbarControls, board } = useWorkBoardLayout({
        view,
        viewId: viewCollection.baseViewId,
        setView,
        groupBy,
        sort,
        onSortChange: handleSortChange,
        workspaceRows,
        listQuery: slimParams,
        onLayoutChange: clearRowSelection,
        rowSelection,
        onRowSelectionChange: setRowSelection,
    });

    const viewToolbar = useMemo(
        () => (
            <ViewToolbar
                view={view}
                onViewChange={setView}
                onViewSelect={handleViewSelect}
                viewConfig={workPageViewConfig}
                builtInViews={builtInViews}
                viewLabels={viewLabels}
                defaultFilters={{}}
                viewCollection={viewCollection}
                initialBaseViewId={initialBaseViewId}
                isColumnSettingsHidden={isBoardLayout}
            >
                {toolbarControls}
            </ViewToolbar>
        ),
        [
            view,
            setView,
            handleViewSelect,
            builtInViews,
            viewLabels,
            viewCollection,
            initialBaseViewId,
            isBoardLayout,
            toolbarControls,
        ],
    );

    useSubNavigationPortalContent("clients-view-toolbar", viewToolbar);

    // Force bulkActions + selection on the rendered view; handleViewChange
    // strips only bulkActions before persisting.
    const viewWithBulkActions = useMemo(
        () => ({
            ...view,
            options: {
                ...view.options,
                bulkActions: true as const,
                selection: true as const,
            },
        }),
        [view],
    );

    const handleOpenOccurrencesDrawer = useCallback((recurrenceId: string) => {
        setOccurrencesScheduleId(recurrenceId);
    }, []);

    const { activeRow, isOpenedByKeyboard, openWorkItem, closeWorkItem } =
        useActiveWorkItem({
            workspaceRows,
            isLoaded: areRowsCurrent,
        });

    const callbacks = useMemo(
        (): ClientsWorkOverviewCallbacks => ({
            onOpenDetails: openWorkItem,
            getViewInActionsLink: getWorkViewInActionsLink,
            getOpenClientLink: getWorkOpenClientLink,
            onOpenOccurrencesDrawer: handleOpenOccurrencesDrawer,
            onGoToTemplate: handleGoToTemplate,
        }),
        [openWorkItem, handleOpenOccurrencesDrawer, handleGoToTemplate],
    );

    const gridRef = useRef<DataGridHandle>(null);

    const workRowSelection = useWorkRowSelection({
        tableData,
        workspaceRows,
        layout: isBoardLayout ? "board" : "list",
        rowSelection,
        setRowSelection,
    });
    const { selectedEngagements, applicationCount } = workRowSelection;
    const selectedCount = selectedEngagements.length;

    const workCommands = useWorkCommands({
        workspaceRows,
        selectedEngagements,
        setRowSelection,
        activeRow,
    });

    const total = getClientsWorkTotal({
        groupBy,
        applicationCount,
        templateCount: tableData?.length ?? 0,
        clientCount,
    });
    const boardPager = hasPaginationControls({
        pageCount,
        currentPage: page,
    }) ? (
        <Pagination
            size="md"
            pageCount={pageCount}
            currentPage={page}
            onPageChange={setPage}
        />
    ) : null;
    const footer = total.count ? (
        <ClientsWorkFooter
            total={total}
            pager={isBoardLayout ? boardPager : <DataGrid.Pager />}
            selectedCount={selectedCount}
            onDeselect={clearRowSelection}
            onOpenBulkActions={workCommands.openCommands}
            viewInActionsLink={
                activeRow ? getWorkViewInActionsLink(activeRow) : undefined
            }
        />
    ) : undefined;

    const emptyView = (
        <ClientsWorkEmptyView
            hasActiveFilters={hasActiveFilters}
            onClearFilters={handleClearFilters}
            onGoToTemplates={handleGoToTemplates}
        />
    );

    return (
        <article className={interfaceStyles.fullHeightContent}>
            <TablePageHeader
                search={filters.search}
                onSearchChange={handleSearchChange}
                isFullPage
                filters={tabFilters}
                onFiltersChange={handleTabFiltersChange}
                availableFilters={AVAILABLE_FILTERS}
            >
                <WorkGroupingControls
                    isBoardLayout={isBoardLayout}
                    groupBy={groupBy}
                    onGroupByChange={handleGroupByChange}
                    expanded={expanded}
                    tableRef={gridRef}
                />
            </TablePageHeader>

            <WorkDetailLayout
                activeRow={activeRow}
                isOpenedByKeyboard={isOpenedByKeyboard}
                onClose={closeWorkItem}
            >
                {isBoardLayout ? (
                    <WorkBoardContent
                        board={board}
                        isError={isError}
                        onRetry={retry}
                        sort={sort}
                        callbacks={callbacks}
                        activeItemId={activeRow?.id ?? null}
                        onOpenItem={openWorkItem}
                        emptyView={emptyView}
                        footer={footer}
                    />
                ) : (
                    <WorkListTable
                        ref={gridRef}
                        rows={tableData}
                        isRefetching={isRefetching}
                        isError={isError}
                        onRetry={retry}
                        expanded={expanded}
                        onExpandedChange={setExpanded}
                        groupBy={groupBy}
                        view={viewWithBulkActions}
                        defaultView={ALL_WORK_VIEW}
                        onViewChange={handleViewChange}
                        sort={sort}
                        onSortChange={handleSortChange}
                        page={page}
                        pageCount={pageCount}
                        onPageChange={setPage}
                        rowSelection={workRowSelection.visibleRowSelection}
                        onRowSelectionChange={
                            workRowSelection.onListRowSelectionChange
                        }
                        onToggleListRow={workRowSelection.toggleListRow}
                        onSelectListRow={workRowSelection.selectListRow}
                        onToggleAllListRows={workRowSelection.toggleAllListRows}
                        activeRow={activeRow}
                        onOpenWorkItem={openWorkItem}
                        callbacks={callbacks}
                        emptyView={emptyView}
                        footer={footer}
                    />
                )}
            </WorkDetailLayout>

            {workCommands.modals}

            <ServiceTemplateScheduleOccurrencesDrawer
                open={!!occurrencesScheduleId}
                schedule={occurrencesSchedule ?? null}
                onClose={() => setOccurrencesScheduleId(null)}
            />
        </article>
    );
};

const ClientsWorkLandingGate: FC = () => {
    const landing = useWorkPageLanding();

    if (!landing) {
        return <PageCoverLoader />;
    }

    return <ClientsWorkContent landing={landing} />;
};

/** The Work page, opened on the view a first visit lands on (per organization). */
export const ClientsWorkPage: FC = () => {
    const { organizationId } = useScope();
    return <ClientsWorkLandingGate key={organizationId} />;
};
